import { useSelector } from 'react-redux';
import {
  selectTransactionFingerprintSettings,
  selectActionEventFingerprintSettings,
  selectTransactionDataSettingsByNativeKey,
  selectActionEventDataSettingsByNativeKey,
  selectDataSettingsById,
} from 'app/modules/dataSettings/selectors';
import {
  FullTxnResponse,
  ShortTxnResponse,
} from 'app/modules/transactions/types';
import {
  CachedActionEventResponse,
  ShortActionEventResponse,
} from 'app/modules/actionEvents/responses';
import { useMemo } from 'react';

type EventType = 'txn' | 'action';
type EventDetails =
  | FullTxnResponse
  | CachedActionEventResponse
  | ShortActionEventResponse
  | ShortTxnResponse;

export const useFingerprintCount = (
  event: EventType,
  details?: EventDetails,
) => {
  const fingerprintSettings = useSelector(
    event === 'txn'
      ? selectTransactionFingerprintSettings
      : selectActionEventFingerprintSettings,
  );

  return useMemo(() => {
    if (!details) return 0;

    return fingerprintSettings.reduce((count, f) => {
      if (!f) return count;

      const isFingerprintBoolean =
        f.data_type === 'BOOLEAN' &&
        f.key_type === 'NATIVE' &&
        f.native_key?.startsWith('fingerprint_');

      if (isFingerprintBoolean) {
        const formattedData = details.formatted_data?.[f.id];
        if (formattedData?.formatted_value === true) {
          return count + 1;
        }
      }
      return count;
    }, 0);
  }, [fingerprintSettings, details]);
};

export const useFingerprintNewlySeen = (
  event: EventType,
  details?: EventDetails,
) => {
  const eventTransactionDataSettingsByNativeKey = useSelector(
    event === 'txn'
      ? selectTransactionDataSettingsByNativeKey
      : selectActionEventDataSettingsByNativeKey,
  );

  return useMemo(() => {
    const newlySeenId =
      eventTransactionDataSettingsByNativeKey?.fingerprint_newly_seen?.id;

    return details && newlySeenId
      ? details.formatted_data?.[newlySeenId]?.formatted_value === true
      : false;
  }, [eventTransactionDataSettingsByNativeKey, details]);
};

export const useHasFingerprintData = (
  event: EventType,
  details?: EventDetails,
) => {
  const dataSettingsById = useSelector(selectDataSettingsById);

  return useMemo(() => {
    if (!details) return false;

    return Object.entries(details.formatted_data).some(([key, value]) => {
      const dataSetting = dataSettingsById[key];
      return (
        dataSetting?.key_type === 'NATIVE' &&
        dataSetting.native_key.includes('fingerprint_') &&
        value.formatted_value != null
      );
    });
  }, [details, dataSettingsById]);
};
